/**
 * Admin panel layout shell.
 *
 * Renders a fixed RTL Arabic header containing:
 *  - The dashboard title "لوحة التحكم".
 *  - The currently-authenticated admin's username (when the JWT payload
 *    exposes one - the local decode is purely cosmetic, the backend is the
 *    authority).
 *  - A "تسجيل الخروج" button that delegates to `useAuth().logout()`. The
 *    logout action already clears the stored token and navigates to
 *    `/admin/login`, so the component itself doesn't touch storage or the
 *    router directly (single source of truth lives in `AuthContext`).
 *
 * Nested admin routes are rendered through `<Outlet />` so this can be used
 * as a layout route in `react-router` v6.
 *
 * Validates: Requirements 4.8
 */

import { Outlet } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';

import styles from './AdminLayout.module.css';

export function AdminLayout(): JSX.Element {
  const { logout, payload } = useAuth();
  const username =
    typeof payload?.username === 'string' && payload.username.length > 0
      ? payload.username
      : null;

  return (
    <div className={styles.container} dir="rtl">
      <header className={styles.header}>
        {/* Brand: Logo + title */}
        <div className={styles.headerBrand}>
          <img src="/logo.png" alt="وصل لي" className={styles.headerLogo} />
          <div className={styles.titleDivider} aria-hidden="true" />
          <h1 className={styles.title}>لوحة التحكم</h1>
        </div>

        <div className={styles.actions}>
          {username !== null ? (
            <span className={styles.username} data-testid="admin-username">
              👤 {username}
            </span>
          ) : null}
          <button
            type="button"
            className={styles.logoutButton}
            onClick={logout}
          >
            تسجيل الخروج
          </button>
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

export default AdminLayout;
